import { Mainnet } from "@usedapp/core"
import { ethers } from "ethers"
import React, { useCallback, useEffect, useState } from "react"
import { botBridgeAbi } from "web3/abi/bot-bridge"
import { botGenesisAbi } from "web3/abi/bot-genesis"
import { contractAddresses } from "web3/addresses"

import { Links } from "config/links"

import { useTailwindBreakpoints } from "components/hooks/use-tailwind-breakpoints"
import { useWeb3Simple } from "components/hooks/web3/use-base"

import { BotCard } from "components/pages/wrap/bot-card"
import { BackgroundImage } from "components/shared/background-img"
import { Box } from "components/shared/box"
import { PolygonIcon } from "components/shared/icons/polygon-icon"
import { Rotating } from "components/shared/icons/rotating"
import { Button } from "components/shared/landing/button"
import { Section } from "components/shared/landing/section"
import { Link } from "components/shared/link"
import { Text } from "components/shared/text"

import { sleep } from "utils/sleep"
import { cutAddress } from "utils/web3/cut-address"

const links = [
  { label: "OpenSea", url: Links.OPENSEA_GENESIS },
  { label: "LooksRare", url: Links.LOOKS_RARE_GENESIS },
  { label: "x2y2", url: Links.X2Y2_GENESIS },
]

export default function Bridge() {
  const { account, connect, connectWalletConnect, connected, disconnect, loading, library } = useWeb3Simple()

  const [botIds, setBotIds] = useState<number[]>([])
  const [botsApproved, setBotsApproved] = useState<boolean>(false)
  const [botsLoading, setBotsLoading] = useState(false)
  const [processing, setProcessing] = useState(false)

  const [selectedBotIds, setSelectedBotIds] = useState<number[]>([])

  const { isMobile } = useTailwindBreakpoints()

  const getBotsContract = async () => {
    if (!account || !connected || !library || !library.getSigner) {
      return
    }
    setBotsLoading(true)

    const signer = library.getSigner()
    const tokenC = new ethers.Contract(contractAddresses.BotGenesis, botGenesisAbi, signer)

    const filterTo = tokenC.filters.Transfer(null, account)
    const eventsTo = await tokenC.queryFilter(filterTo)
    const filterFrom = tokenC.filters.Transfer(account)
    const eventsFrom = await tokenC.queryFilter(filterFrom)

    const idsFromMe = eventsFrom?.map(e => parseInt(e.topics[3]))
    const idsToExclude = [...idsFromMe]
    const idsToMe = eventsTo?.map(e => parseInt(e.topics[3]))
    const tokenIdsInWallet = idsToMe
      .map(i => {
        if (idsToExclude.length) {
          const itemIndex = idsToExclude.indexOf(i)
          if (itemIndex !== -1) {
            idsToExclude.splice(itemIndex, 1)
            return null
          }
        }
        return i
      })
      .filter(e => Number.isInteger(e))

    const genesisContract = new ethers.Contract(contractAddresses.BotGenesis, botGenesisAbi, signer)
    const approved = await genesisContract.isApprovedForAll(account, contractAddresses.BotBridge)
    setBotsApproved(approved)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setBotIds(tokenIdsInWallet)
    await sleep(1000)
    setBotsLoading(false)
  }

  useEffect(() => {
    setBotIds([])
    setBotsApproved(false)
    getBotsContract()
  }, [account])

  const approveAll = useCallback(async () => {
    if (!connected || !library || !library.getSigner) {
      return
    }
    setProcessing(true)
    const signer = library.getSigner()
    const genesisContract = new ethers.Contract(contractAddresses.BotGenesis, botGenesisAbi, signer)
    const tx = await genesisContract.setApprovalForAll(contractAddresses.BotBridge, true)
    await library.waitForTransaction(tx?.hash)
    setBotsApproved(true)
    setProcessing(false)
  }, [connected, account])

  const clickSelectBot = (id: number) => {
    const index = selectedBotIds.findIndex(i => i === id)
    const _selectedBotIds = [...selectedBotIds]
    if (index === -1) {
      _selectedBotIds.push(id)
    } else {
      _selectedBotIds.splice(index, 1)
    }
    setSelectedBotIds(_selectedBotIds)
  }

  const clearSelectedBots = () => setSelectedBotIds([])

  const bridge = async () => {
    if (!connected || !library || !library.getSigner) {
      return
    }
    setProcessing(true)
    const signer = library.getSigner()
    const bridgeContract = new ethers.Contract(contractAddresses.BotBridge, botBridgeAbi, signer)
    const tx = await bridgeContract.bridgeBots(account, selectedBotIds)
    await library.waitForTransaction(tx?.hash)
    clearSelectedBots()
    setProcessing(false)
    getBotsContract()
  }

  return (
    <Box className="flex-1 mt-[80px]">
      <BackgroundImage imageSrc="/images/pages/wrap/back.png" hideMobile className="bg-auto md:bg-repeat-x md:bg-cover">
        <Box className="md:p-6">
          <Section>
            <Box className="md:flex-row gap-4 md:gap-6">
              <Box className="gap-4 md:gap-6 flex-1 mt-2 md:mt-0">
                <Text className="text-h2">
                  <PolygonIcon w={38} h={38} className="inline" /> Polygon Bots Bridge
                </Text>
                <Text>
                  Transfer your Genesis Bots to Polygon!
                  <br />
                  To play Cryptobots, all players have to transfer their Bots from ETH to the Polygon network.
                  <br />
                  The Cryptobots team covers the Polygon network fees for the transfer.
                  <br />
                  Transfer Bots now.
                </Text>
                <Text className="text-h4">Buy Cryptobots on</Text>
                <Box row className="gap-6 -mt-2">
                  {links.map(l => (
                    <Link href={l.url} key={l.url} target="_blank" className="text-base underline">
                      {l.label}
                    </Link>
                  ))}
                </Box>
              </Box>
              <Box className="gap-4 md:gap-6 flex-1">
                <Box className="backdrop-blur-md bg-white/10 p-6 rounded-2xl text-white gap-2 flex-1">
                  <Text className="text-h4">Connected wallet</Text>
                  <Box row className="justify-between items-center">
                    {account && <Text className="text-h4 opacity-80">{cutAddress(account)}</Text>}
                    {connected ? (
                      <Button onClick={disconnect} type="secondary">
                        Disconnect
                      </Button>
                    ) : (
                      <Box center className="w-full">
                        <Button onClick={isMobile ? connectWalletConnect : connect} type="primary" disabled={loading}>
                          {loading ? "Connecting.." : `Connect ${isMobile ? "Wallet" : "Metamask"}`}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box className="backdrop-blur-md bg-white/10 p-6 rounded-2xl text-white flex-1">
                  <Text className="text-h4">Genesis</Text>
                  <Text className="opacity-80 mt-2 text-base">Bots:</Text>
                  <Text className="text-h3">{connected ? botIds?.length : "-"}</Text>
                </Box>
              </Box>
            </Box>
          </Section>
          {botsLoading && (
            <Box row center>
              <Rotating className="animate-spin w-[2rem]" />
            </Box>
          )}

          {connected && botIds.length > 0 && (
            <Section className="-mt-10">
              <Box row className="justify-between">
                <Box>
                  <Text className="text-h3">My Genesis Bots</Text>
                  <Text className="text-body">Please select bot to bridge</Text>
                </Box>

                {selectedBotIds.length > 0 && (
                  <Box
                    center
                    className="fixed z-10 bg-black/20 h-20 md:h-auto md:bg-black/0 w-full md:w-auto md:relative left-0 bottom-0"
                  >
                    <Box row className="gap-4 md:gap-6">
                      <Button size="sm" onClick={botsApproved ? bridge : approveAll} loading={processing}>
                        {!botsApproved ? "Approve" : "Bridge selected"}
                      </Button>
                      <Button size="sm" type="secondary" onClick={clearSelectedBots} className="uppercase">
                        Clear selection
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
              <Box row className="gap-4 mt-6 flex-wrap justify-center md:justify-start">
                {botIds?.map(b => (
                  <BotCard
                    isGenesis
                    key={b}
                    id={b}
                    blockchainApproved={true}
                    showButtons={false}
                    onSelect={clickSelectBot}
                    selected={selectedBotIds.some(i => Number(b) === i)}
                  />
                ))}
              </Box>
            </Section>
          )}
        </Box>
      </BackgroundImage>
    </Box>
  )
}

Bridge.requireNetwork = true
Bridge.chain = Mainnet
